import { defineComponent, ref, computed, h } from 'vue'
import icon from './icons'
import { firstDocumentName } from './first_document_name'
import { ROLE_DEFAULTS, defaultRoleName } from './signer_roles'
import { C, s, modalHeader, section, fieldLabel, dropzoneShell, uploadSplitButton, menuItem, signerRoleSelect, applyRoleDefaults, deleteRecipientButton, messageTemplateControls } from './creator_modal_ui'
import { loadMessageTemplates, saveMessageTemplate } from './message_templates'

// A blank recipient at position `index`, prefilled from the role that sits there. Position 0 is
// ApTask's onboarding mailbox, which is why the first row of a new envelope arrives filled in
// (and still editable — nothing here is readonly).
function blankRecipient (index) {
  const roleName = defaultRoleName(index)
  const defaults = ROLE_DEFAULTS[roleName] || {}

  return {
    id: Date.now() + index,
    name: defaults.default_name || '',
    email: defaults.email || '',
    role: 'signer',
    roleName
  }
}

export default defineComponent({
  name: 'EnvelopeCreator',
  props: { onClose: Function },
  setup(props) {
    const step = ref(1)
    const docs = ref([])
    const recipients = ref([blankRecipient(0)])
    // Sequential signing is the default: recipients sign one at a time, and each is only
    // invited once the one before them is done. Untick for everyone at once.
    const signingOrderEnabled = ref(true)
    const message = ref({ subject: '', body: '' })

    // Saved email wording (MessageTemplatesController). Fetched once when the modal mounts; a
    // failure leaves the list empty and the picker says so, because this is a convenience beside
    // the subject/body inputs and must not block the form it sits in.
    const messageTemplates = ref([])
    const messageSaveState = ref({ open: false, name: '', saving: false, error: '' })

    loadMessageTemplates().then(list => { messageTemplates.value = list })

    async function saveCurrentMessage () {
      const state = messageSaveState.value

      state.saving = true
      state.error = ''

      try {
        const saved = await saveMessageTemplate({
          name: state.name.trim(),
          subject: message.value.subject || '',
          body: message.value.body || ''
        })

        messageTemplates.value = [...messageTemplates.value, saved]
          .sort((a, b) => a.name.localeCompare(b.name))
        state.open = false
        state.name = ''
      } catch (e) {
        state.error = e.message
      } finally {
        state.saving = false
      }
    }
    const envelopeCategory = ref('')
    const envelopeType = ref('')
    const fileInput = ref(null)
    const submitting = ref(false)
    // Persist token across back navigation
    if (!sessionStorage.getItem('envelope_creation_token')) {
      sessionStorage.setItem('envelope_creation_token', Math.random().toString(36).substring(2))
    }
    const creationToken = ref(sessionStorage.getItem('envelope_creation_token'))

    // Reset submitting state when user navigates back to this page
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') submitting.value = false
      })
      window.addEventListener('pageshow', () => { submitting.value = false })
    }
    const showUploadDropdown = ref(false)

    // Existing template picker state
    const showTemplatePicker = ref(false)
    const allTemplates = ref([])
    const filteredTemplates = ref([])
    const templateSearch = ref('')
    const selectedExistingTemplate = ref([])
    const selectionOrder = ref([])
    const loadingTemplates = ref(false)
    // Explicit role mapping (Option 2): final recipients + which source signer maps to which.
    const finalRecipients = ref([])   // [{ key, name, email, role }]
    const roleAssignments = ref({})   // { sourceSubmitterUuid: recipientKey }

    // Advanced Options state
    const showAdvanced = ref(false)
    const advActiveSection = ref('recipient-privileges')
    const advCanSignOnPaper = ref(false)
    const advCanDelegate = ref(true)
    const advReminderEnabled = ref(false)
    const advReminderDays = ref(3)
    const advReminderFrequency = ref(3)
    const advExpirationDays = ref(120)
    const advExpirationAlertDays = ref(0)
    const advPasswordEnabled = ref(false)
    const advPassword = ref('')
    const advAllowRecipientsEdit = ref(true)
    const advAllowMessageEdit = ref(true)

    const advSections = [
      { id: 'recipient-privileges', label: 'Recipient Privileges' },
      { id: 'reminders', label: 'Reminders' },
      { id: 'expiration', label: 'Expiration' },
      { id: 'template-password', label: 'Template Password' },
      { id: 'template-modification', label: 'Template Modification' },
    ]

    function scrollToSection(id) {
      advActiveSection.value = id
      const el = document.getElementById('env-adv-section-' + id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const canNext = computed(() => {
      const hasDoc = docs.value.length > 0 || selectedExistingTemplate.value.length > 0
      // Email is required: without it the envelope can't be sent and the server
      // silently bounces back to home. Name + valid email for every recipient.
      // When merging templates, validate the mapped final recipients instead.
      const list = selectedExistingTemplate.value.length > 0 ? finalRecipients.value : recipients.value
      const hasRecipients = list.length > 0 &&
        list.every(r => (r.name || '').trim() && EMAIL_RE.test((r.email || '').trim()))
      return hasDoc && hasRecipients
    })


    // The subject follows the first document added, whichever kind it is. An upload used to fill
    // it for neither. Blank only, so anything typed by hand still wins. The envelope's own name
    // is no longer asked for here — the server names it after the same document
    // (EnvelopesController#prepare), which is all the removed field ever did.
    function autofillNameAndSubject() {
      const docName = firstDocumentName(selectionOrder.value, docs.value, selectedExistingTemplate.value)

      if (!docName) return

      if (!message.value.subject.trim()) message.value.subject = `Please Complete DocuSeal – ${docName}`
    }

    function addFiles(fileList) {
      Array.from(fileList).forEach((f, i) => {
        const docId = Date.now() + i
        docs.value.push({ id: docId, name: f.name, file: f })
        selectionOrder.value.push({ type: 'file', id: docId })
      })
      autofillNameAndSubject()
      showUploadDropdown.value = false
    }

    function addRecipient() {
      recipients.value.push(blankRecipient(recipients.value.length))
    }

    // Every row carries a delete control, the only row included. Deleting that last one clears
    // it back to a fresh default rather than leaving an empty table: role one is always ApTask's
    // onboarding mailbox, so "no recipients at all" is not a state this form can send from
    // (canNext would block it) and an empty table with only a header reads as broken.
    function removeRecipient(id) {
      const remaining = recipients.value.filter(r => r.id !== id)

      recipients.value = remaining.length ? remaining : [blankRecipient(0)]
    }

    const dragIndex = ref(null)

    function onDragStart(index) { dragIndex.value = index }

    // Both recipient lists reorder the same way; only the list being dragged differs.
    function reorder(listRef, e, index) {
      e.preventDefault()
      if (dragIndex.value === null || dragIndex.value === index) return
      const list = [...listRef.value]
      const dragged = list.splice(dragIndex.value, 1)[0]
      list.splice(index, 0, dragged)
      listRef.value = list
      dragIndex.value = index
    }

    function onDragOver(e, index) { reorder(recipients, e, index) }

    function onRoleDragOver(e, index) { reorder(finalRecipients, e, index) }

    // The Order column is a routing NUMBER, the same one DocuSign uses: a lower number is
    // invited first, and two rows sharing a number are invited together. It is typed, not only
    // dragged -- dragging cannot express "these two at the same time", which is the whole reason
    // the column is editable. A drag still works and simply renumbers the list 1..n so the
    // numbers keep matching the rows.
    function renumber (listRef) {
      listRef.value.forEach((r, i) => { r.order = i + 1 })
    }

    // Clamped to 1..rows: a number above the row count sorts the same as the last row while
    // reading as an order that does not exist, and 0 collides with "no order set" server-side.
    function setOrder (listRef, row, value) {
      const parsed = parseInt(value, 10)

      row.order = Math.min(Math.max(Number.isNaN(parsed) ? 1 : parsed, 1), listRef.value.length)
    }

    // The Order cell for one row of either recipient table.
    function orderCell (listRef, row, index) {
      if (!signingOrderEnabled.value) {
        return h('div', { style: `width:28px;height:28px;border-radius:6px;background:${C.primarySoft};color:${C.muted};font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center` }, '–')
      }

      return h('input', {
        type: 'number',
        min: 1,
        max: listRef.value.length,
        value: row.order || (index + 1),
        title: 'Signing order. Give two recipients the same number to invite them at the same time.',
        onChange: e => setOrder(listRef, row, e.target.value),
        style: `width:38px;height:28px;box-sizing:border-box;padding:0 4px;border:1px solid ${C.border};border-radius:6px;background:${C.primarySoft};color:${C.primary};font-size:12px;font-weight:700;text-align:center;font-family:inherit`
      })
    }

    function onDragEnd() {
      if (dragIndex.value !== null) {
        renumber(selectedExistingTemplate.value.length > 0 ? finalRecipients : recipients)
      }

      dragIndex.value = null
    }

    // Selected templates in pick order (matches the server-side merge order).
    function orderedSelectedTemplates() {
      const byId = {}
      selectedExistingTemplate.value.forEach(t => { byId[t.id] = t })
      const ordered = selectionOrder.value
        .filter(s => s.type === 'template' && byId[s.id])
        .map(s => byId[s.id])
      selectedExistingTemplate.value.forEach(t => { if (!ordered.includes(t)) ordered.push(t) })
      return ordered
    }

    const normRole = (str) => (str || '').trim().toLowerCase()
    const newRecipientKey = () => 'r-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6)

    // Flatten the picked templates' signers into source roles, in pick order.
    function sourceRoles() {
      const roles = []
      orderedSelectedTemplates().forEach(t => {
        (t.submitters || []).forEach(sub => {
          if (sub && sub.uuid) roles.push({ uuid: sub.uuid, roleName: sub.name || 'Signer', templateName: t.name, defaultName: sub.default_name, defaultEmail: sub.email })
        })
      })
      return roles
    }

    function pruneRecipients() {
      const used = new Set(Object.values(roleAssignments.value))
      finalRecipients.value = finalRecipients.value.filter(r => used.has(r.key) || r.manual)
    }

    // Default mapping: group source roles by (normalized) name — one recipient per distinct
    // role. Same name across templates merges; distinct names stay separate. Preserves any
    // assignment or recipient edit the user already made.
    function rebuildRoleMapping() {
      const roles = sourceRoles()
      if (roles.length === 0) { finalRecipients.value = []; roleAssignments.value = {}; return }
      const nameToKey = {}
      finalRecipients.value.forEach(r => { nameToKey[normRole(r.roleName)] = r.key })
      const assignments = {}
      roles.forEach(role => {
        let key = roleAssignments.value[role.uuid]
        if (!key || !finalRecipients.value.find(r => r.key === key)) {
          key = nameToKey[normRole(role.roleName)]
          if (!key) {
            key = newRecipientKey()
            nameToKey[normRole(role.roleName)] = key
            // roleName is the signing role from the template; name is the person, typed below
            // (prefilled when the template names a usual signer for that role).
            finalRecipients.value.push({ key, roleName: role.roleName, name: role.defaultName || '', email: role.defaultEmail || '', role: 'signer' })
          }
        }
        assignments[role.uuid] = key
      })
      roleAssignments.value = assignments
      pruneRecipients()
    }

    function assignRole(uuid, key) {
      if (key === '__new__') {
        const role = sourceRoles().find(r => r.uuid === uuid)
        key = newRecipientKey()
        finalRecipients.value.push({ key, roleName: role ? role.roleName : 'Recipient', name: '', email: '', role: 'signer' })
      }
      roleAssignments.value = { ...roleAssignments.value, [uuid]: key }
      pruneRecipients()
    }

    // The role list replaces the plain recipient list once a template is picked, so it needs
    // its own add/remove. Manual entries survive pruneRecipients: no source role points at
    // them, which is exactly what prune uses to decide a recipient is dead.
    function addFinalRecipient() {
      const roleName = defaultRoleName(finalRecipients.value.length)
      const defaults = ROLE_DEFAULTS[roleName] || {}

      finalRecipients.value.push({
        key: newRecipientKey(),
        manual: true,
        roleName,
        name: defaults.default_name || '',
        email: defaults.email || '',
        role: 'signer'
      })
    }

    // Every recipient is deletable, mapped source roles included. Those roles used to make a
    // recipient permanent (dropping it would leave its fields pointing at nothing), which meant
    // the row the sender most wanted to remove was the one row with no delete control. Instead
    // of blocking, the roles move: they are reassigned to the first recipient left, so the
    // fields follow a signer that still exists.
    function removeFinalRecipient(key) {
      const remaining = finalRecipients.value.filter(r => r.key !== key)

      if (remaining.length === 0) {
        // Deleting the last one rebuilds the picked templates' default mapping rather than
        // leaving every source role pointing at nothing.
        finalRecipients.value = []
        roleAssignments.value = {}
        rebuildRoleMapping()

        return
      }

      const fallback = remaining[0].key
      const reassigned = { ...roleAssignments.value }

      Object.keys(reassigned).forEach(uuid => {
        if (reassigned[uuid] === key) reassigned[uuid] = fallback
      })

      finalRecipients.value = remaining
      roleAssignments.value = reassigned
    }

    // Role mapping is hidden for now: the default name-based mapping still runs and is
    // still posted as role_map, so fields land on the right signer — only the UI for
    // overriding that mapping is suppressed. Flip to true to bring it back.
    const SHOW_ROLE_MAPPING = false

    // Order | Name | Email | Signer | Action | remove. Shared by the header and every row of the
    // plain recipient list so the two cannot drift out of alignment.
    const plainCols = '40px 1.1fr 1.2fr 130px 120px 36px'

    // Role-mapping UI, shown in place of the plain recipient list when templates are merged.
    function renderRoleMapping() {
      const cols = '40px 1fr 1.2fr 1.3fr 120px 36px'
      return h('div', { style: 'display:flex;flex-direction:column;gap:14px' }, [
        SHOW_ROLE_MAPPING && h('div', { style: 'display:flex;flex-direction:column;gap:6px' },
          sourceRoles().map(role => h('div', {
            key: role.uuid,
            style: `display:flex;align-items:center;gap:10px;padding:8px 12px;border:1px solid ${C.border};border-radius:8px;background:#fff`
          }, [
            h('div', { style: 'flex:1;min-width:0' }, [
              h('div', { style: `font-size:13px;font-weight:600;color:${C.text};white-space:nowrap;overflow:hidden;text-overflow:ellipsis` }, role.roleName),
              h('div', { style: 'font-size:11px;color:#9CA3AF;white-space:nowrap;overflow:hidden;text-overflow:ellipsis' }, role.templateName)
            ]),
            h('span', { style: `color:${C.muted};flex-shrink:0` }, '→'),
            h('select', {
              value: roleAssignments.value[role.uuid] || '',
              onChange: e => assignRole(role.uuid, e.target.value),
              style: s.input + ';width:auto;min-width:150px;cursor:pointer;flex-shrink:0'
            }, [
              ...finalRecipients.value.map(r => h('option', { value: r.key }, r.name ? `${r.roleName} — ${r.name}` : (r.roleName || 'Recipient'))),
              h('option', { value: '__new__' }, '+ New recipient')
            ])
          ]))
        ),
        h('label', { style: 'display:flex;align-items:flex-start;gap:8px;cursor:pointer;width:fit-content' }, [
          h('input', { type: 'checkbox', checked: signingOrderEnabled.value, onChange: e => { signingOrderEnabled.value = e.target.checked }, style: `width:15px;height:15px;margin-top:2px;cursor:pointer;accent-color:${C.primary}` }),
          h('div', null, [
            h('div', { style: `font-size:13px;font-weight:600;color:${C.text}` }, 'Set signing order'),
            h('div', { style: `font-size:12px;color:${C.muted};margin-top:2px` }, 'Recipients will be asked to sign in the order listed below.')
          ])
        ]),
        h('div', { style: s.tableWrap }, [
          h('div', { style: s.tableHead + `;grid-template-columns:${cols}` }, [
            h('span', null, 'Order'), h('span', null, 'Role'), h('span', null, 'Name *'),
            h('span', null, 'Email *'), h('span', null, 'Action'), h('span', null, '')
          ]),
          ...finalRecipients.value.map((r, i) => h('div', {
            key: r.key,
            draggable: signingOrderEnabled.value,
            onDragstart: () => signingOrderEnabled.value && onDragStart(i),
            onDragover: e => signingOrderEnabled.value && onRoleDragOver(e, i),
            onDragend: onDragEnd,
            style: s.tableRow + `;grid-template-columns:${cols};${i === finalRecipients.value.length - 1 ? 'border-bottom:none' : ''}`
          }, [
            orderCell(finalRecipients, r, i),
            // Every row picks from the same two roles, whether it was added by hand or came from
            // a picked template. Template-derived rows used to render their role as fixed text,
            // which meant an envelope built on one of the templates that predate these two roles
            // was stuck with whatever that template calls the role ("OB", "First Party") and no
            // way to say "this is the Employee". signerRoleSelect keeps the template's own value
            // as a third option, so the old name is offered rather than silently rewritten.
            //
            // Safe to change here because the clone remaps fields by uuid, not by name:
            // prepare_from_existing builds the cloned template's submitters from these role_names
            // and routes source signers through role_map, so renaming moves the label without
            // moving a field. The source template itself is never touched.
            signerRoleSelect(r.roleName, name => { r.roleName = name; applyRoleDefaults(r, name) }),
            h('input', { value: r.name, placeholder: 'Full name', onInput: e => { r.name = e.target.value }, style: s.input }),
            h('input', { value: r.email, placeholder: 'email@example.com', type: 'email', onInput: e => { r.email = e.target.value }, style: s.input }),
            h('select', { value: r.role, onChange: e => { r.role = e.target.value }, style: s.input + ';cursor:pointer' }, [
              h('option', { value: 'signer' }, 'Needs to Sign'),
              h('option', { value: 'cc' }, 'Receives a Copy'),
              h('option', { value: 'viewer' }, 'Needs to View')
            ]),
            deleteRecipientButton(() => removeFinalRecipient(r.key))
          ]))
        ]),
        h('button', { onClick: addFinalRecipient, style: s.addLink }, [icon('plus', 14), ' Add Recipient'])
      ])
    }

    function removeDoc(id) {
      docs.value = docs.value.filter(d => d.id !== id)
    }

    async function openTemplatePicker() {
      showUploadDropdown.value = false
      showTemplatePicker.value = true
      loadingTemplates.value = true
      try {
        const resp = await fetch('/templates/list_all', { headers: { 'Accept': 'application/json' } })
        if (resp.ok) {
          const list = await resp.json()
          allTemplates.value = Array.isArray(list) ? list.filter(t => t.external_id !== 'envelope_template') : []
          filteredTemplates.value = [...allTemplates.value]
        }
      } catch (err) {
      } finally {
        loadingTemplates.value = false
      }
    }

    function filterTemplates() {
      const q = templateSearch.value.toLowerCase()
      filteredTemplates.value = allTemplates.value.filter(t => t.name.toLowerCase().includes(q))
    }

    // Multi-select: toggle a template in/out of the selection, keeping the picker open.
    // Order added = merge hierarchy; the selected list below can then be reordered by drag.
    function toggleTemplateSelection(t) {
      if (selectedExistingTemplate.value.find(x => x.id === t.id)) {
        selectedExistingTemplate.value = selectedExistingTemplate.value.filter(x => x.id !== t.id)
        selectionOrder.value = selectionOrder.value.filter(s => !(s.type === 'template' && s.id === t.id))
      } else {
        selectedExistingTemplate.value = [...selectedExistingTemplate.value, t]
        selectionOrder.value.push({ type: 'template', id: t.id })
      }
      // The server appends "_<uuid>" to the name and applies the same branded subject as a
      // fallback, so this only has to get the blank-field case right.
      if (selectedExistingTemplate.value.length > 0) {
        autofillNameAndSubject()
        rebuildRoleMapping()
      } else {
        recipients.value = [blankRecipient(0)]
      }
    }

    // Drag-reorder the selected templates. Keeps selectionOrder's template entries in sync
    // (file entries stay in place) and refreshes recipient labels once the drop settles.
    const tplDragIndex = ref(null)
    function syncTemplateSelectionOrder() {
      const ids = selectedExistingTemplate.value.map(t => t.id)
      let ti = 0
      selectionOrder.value = selectionOrder.value.map(s =>
        s.type === 'template' ? { type: 'template', id: ids[ti++] } : s)
    }
    function onTplDragStart(i) { tplDragIndex.value = i }
    function onTplDragOver(e, i) {
      e.preventDefault()
      if (tplDragIndex.value === null || tplDragIndex.value === i) return
      const list = [...selectedExistingTemplate.value]
      const [moved] = list.splice(tplDragIndex.value, 1)
      list.splice(i, 0, moved)
      selectedExistingTemplate.value = list
      syncTemplateSelectionOrder()
      tplDragIndex.value = i
    }
    function onTplDragEnd() { tplDragIndex.value = null; rebuildRoleMapping() }

    function proceedToEditor() {
      if (!canNext.value) return
      submitting.value = true

      sessionStorage.setItem('envelope_recipients', JSON.stringify(recipients.value))
      sessionStorage.setItem('envelope_message', JSON.stringify(message.value))

      const form = document.createElement('form')
      form.method = 'POST'
      form.action = '/prepare'
      form.enctype = 'multipart/form-data'
      form.style.display = 'none'

      function addHidden(name, value) {
        const i = document.createElement('input')
        i.type = 'hidden'; i.name = name; i.value = value
        form.appendChild(i)
      }

      addHidden('authenticity_token', document.querySelector('meta[name=csrf-token]')?.content || '')
      addHidden('creation_token', creationToken.value)
      addHidden('selection_order', JSON.stringify(selectionOrder.value))

      selectedExistingTemplate.value.forEach(t => {
        const f = document.createElement('input')
        f.type = 'hidden'; f.name = 'existing_template_ids[]'; f.value = t.id
        form.appendChild(f)
      })

      if (docs.value.length > 0) {
        const fileTransfer = new DataTransfer()
        docs.value.forEach(d => fileTransfer.items.add(d.file))
        const fileField = document.createElement('input')
        fileField.type = 'file'; fileField.name = 'files[]'; fileField.multiple = true
        fileField.files = fileTransfer.files
        form.appendChild(fileField)
        addHidden('folder_name', '')
      }

      if (selectedExistingTemplate.value.length > 0) {
        // Explicit role mapping: recipients carry a key; role_map routes source signers.
        addHidden('recipients', JSON.stringify(finalRecipients.value.map((r, i) => ({
          key: r.key, name: r.name, email: r.email, role: r.role, role_name: r.roleName,
          signing_order: signingOrderEnabled.value ? (r.order || i + 1) : null
        }))))
        addHidden('role_map', JSON.stringify(roleAssignments.value))
      } else {
        addHidden('recipients', JSON.stringify(recipients.value.map((r, i) => ({
          name: r.name, email: r.email, role: r.role,
          role_name: r.roleName || null, submitterUuid: r.submitterUuid || null,
          signing_order: signingOrderEnabled.value ? (r.order || i + 1) : null
        }))))
      }
      addHidden('subject', message.value.subject || '')
      addHidden('body', message.value.body || '')
      addHidden('envelope_category', envelopeCategory.value || '')
      addHidden('envelope_type', envelopeType.value || '')
      addHidden('signing_order', signingOrderEnabled.value ? 'true' : 'false')

      // Advanced Options
      addHidden('adv_can_sign_on_paper',     advCanSignOnPaper.value     ? '1' : '0')
      addHidden('adv_can_delegate',          advCanDelegate.value        ? '1' : '0')
      addHidden('adv_reminder_enabled',      advReminderEnabled.value    ? '1' : '0')
      addHidden('adv_reminder_days',         advReminderDays.value)
      addHidden('adv_reminder_frequency',    advReminderFrequency.value)
      addHidden('adv_expiration_days',       advExpirationDays.value)
      addHidden('adv_expiration_alert_days', advExpirationAlertDays.value)
      addHidden('adv_password_enabled',      advPasswordEnabled.value    ? '1' : '0')
      addHidden('adv_password',              advPasswordEnabled.value ? advPassword.value : '')
      addHidden('adv_allow_recipients_edit', advAllowRecipientsEdit.value ? '1' : '0')
      addHidden('adv_allow_message_edit',    advAllowMessageEdit.value   ? '1' : '0')

      document.body.appendChild(form)
      form.submit()
    }

    function dsToggle(checked, onChange) {
      return h('div', {
        onClick: () => onChange(!checked),
        style: `position:relative;display:inline-flex;align-items:center;width:44px;height:24px;border-radius:12px;cursor:pointer;transition:.2s;background:${checked ? C.primary : '#D1D5DB'};flex-shrink:0`
      }, [
        h('span', {
          style: `position:absolute;width:18px;height:18px;border-radius:50%;background:#fff;transition:.2s;left:${checked ? '23px' : '3px'};box-shadow:0 1px 3px rgba(0,0,0,.3)`
        })
      ])
    }

    function renderAdvancedPanel() {
      const sectionStyle = 'padding:32px 40px;border-bottom:1px solid #E5E7EB'
      const titleStyle = 'font-size:20px;font-weight:400;color:#1a1a2e;margin:0 0 6px'
      const subtitleStyle = 'font-size:14px;color:#4B5563;margin:0 0 20px'
      const fieldLabelStyle = 'font-size:13px;color:#374151;margin-bottom:4px;font-weight:500'

      const expiryDate = new Date()
      expiryDate.setDate(expiryDate.getDate() + advExpirationDays.value)
      const expiryStr = expiryDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })

      return h('div', {
        style: 'position:fixed;inset:0;z-index:10000;background:#fff;display:flex;flex-direction:column'
      }, [
        // Top bar
        h('div', { style: 'display:flex;align-items:center;justify-content:space-between;padding:0 24px;height:56px;border-bottom:1px solid #E5E7EB;flex-shrink:0' }, [
          h('h1', { style: 'font-size:20px;font-weight:400;color:#1a1a2e;margin:0' }, 'Advanced Options'),
          h('button', {
            onClick: () => { showAdvanced.value = false },
            style: 'background:none;border:none;cursor:pointer;color:#6B7280;font-size:24px;line-height:1;padding:4px'
          }, icon('x', 14))
        ]),

        // Body
        h('div', { style: 'display:flex;flex:1;overflow:hidden' }, [
          // Left sidebar
          h('nav', { style: 'width:200px;border-right:1px solid #E5E7EB;padding:16px 0;flex-shrink:0;overflow-y:auto' },
            advSections.map(sec =>
              h('div', {
                key: sec.id,
                onClick: () => scrollToSection(sec.id),
                style: `padding:10px 20px;font-size:14px;cursor:pointer;color:${advActiveSection.value === sec.id ? C.primary : '#374151'};font-weight:${advActiveSection.value === sec.id ? '600' : '400'};border-left:${advActiveSection.value === sec.id ? '3px solid ' + C.primary : '3px solid transparent'}`
              }, sec.label)
            )
          ),

          // Right scrollable content
          h('div', { style: 'flex:1;overflow-y:auto' }, [

            // Recipient Privileges
            h('div', { id: 'env-adv-section-recipient-privileges', style: sectionStyle }, [
              h('div', { style: 'display:flex;align-items:flex-start;justify-content:space-between' }, [
                h('div', null, [
                  h('h2', { style: titleStyle }, 'Recipient Privileges'),
                  h('p', { style: subtitleStyle }, 'Give recipients options for how they sign.'),
                ]),
                h('span', { style: 'color:#9CA3AF;display:flex' }, icon('lock', 18))
              ]),
              h('label', { style: 'display:flex;align-items:flex-start;gap:10px;cursor:pointer' }, [
                h('input', { type: 'checkbox', checked: advCanDelegate.value, onChange: e => { advCanDelegate.value = e.target.checked }, style: `width:16px;height:16px;margin-top:2px;accent-color:${C.primary};flex-shrink:0` }),
                h('div', { style: 'font-size:14px;color:#1a1a2e' }, 'Recipients can change signing responsibility or assign a delegate')
              ]),
            ]),

            // Reminders
            h('div', { id: 'env-adv-section-reminders', style: sectionStyle }, [
              h('h2', { style: titleStyle }, 'Reminders'),
              h('p', { style: subtitleStyle }, 'Follow up with automatic reminders. Signers will receive emails until they sign or decline the envelope.'),
              h('div', { style: 'display:flex;align-items:center;gap:10px;margin-bottom:20px' }, [
                dsToggle(advReminderEnabled.value, v => { advReminderEnabled.value = v }),
                h('span', { style: 'font-size:14px;color:#374151' }, 'Turn on auto reminders')
              ]),
              advReminderEnabled.value && h('div', { style: 'display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:8px' }, [
                h('div', null, [
                  h('div', { style: fieldLabelStyle }, 'Send first reminder'),
                  h('select', {
                    value: advReminderDays.value,
                    onChange: e => { advReminderDays.value = parseInt(e.target.value) },
                    style: s.input + ';background:#fff;cursor:pointer'
                  }, [1,2,3,5,7,10,14].map(d => h('option', { value: d }, `${d} day${d>1?'s':''} after sending`)))
                ]),
                h('div', null, [
                  h('div', { style: fieldLabelStyle }, 'Send reminder every'),
                  h('select', {
                    value: advReminderFrequency.value,
                    onChange: e => { advReminderFrequency.value = parseInt(e.target.value) },
                    style: s.input + ';background:#fff;cursor:pointer'
                  }, [1,2,3,5,7].map(d => h('option', { value: d }, `${d} day${d>1?'s':''}`)))
                ])
              ])
            ]),

            // Expiration
            h('div', { id: 'env-adv-section-expiration', style: sectionStyle }, [
              h('h2', { style: titleStyle }, 'Expiration'),
              h('p', { style: subtitleStyle }, 'By default, envelopes expire after 120 days. Recipients can no longer view or sign an envelope after it expires.'),
              h('div', { style: 'display:grid;grid-template-columns:1fr 1fr;gap:20px;align-items:end' }, [
                h('div', null, [
                  h('div', { style: fieldLabelStyle }, 'Days until envelope expires'),
                  h('select', {
                    value: [7,14,30,60,90,120,180,365].includes(advExpirationDays.value) ? advExpirationDays.value : 'custom',
                    onChange: e => { if (e.target.value !== 'custom') advExpirationDays.value = parseInt(e.target.value) },
                    style: s.input + ';background:#fff;cursor:pointer'
                  }, [
                    ...[7,14,30,60,90,120,180,365].map(d => h('option', { value: d }, `${d} days`)),
                    h('option', { value: 'custom' }, 'Custom days')
                  ])
                ]),
                h('div', null, [
                  h('div', { style: fieldLabelStyle }, ['Custom number of days ', h('span', { style: 'color:#DC2626' }, '*')]),
                  h('input', {
                    type: 'number', min: 1, max: 999,
                    value: advExpirationDays.value,
                    onInput: e => { advExpirationDays.value = Math.max(1, parseInt(e.target.value) || 1) },
                    style: s.input
                  })
                ])
              ]),
              h('div', { style: 'margin-top:8px;font-size:13px;color:#374151' }, [
                'Envelopes will expire on: ',
                h('strong', null, expiryStr)
              ]),
              h('div', { style: 'display:grid;grid-template-columns:1fr 1fr;gap:20px;align-items:end;margin-top:20px' }, [
                h('div', null, [
                  h('div', { style: fieldLabelStyle }, 'Send alert before expiry'),
                  h('select', {
                    value: advExpirationAlertDays.value,
                    onChange: e => { advExpirationAlertDays.value = parseInt(e.target.value) },
                    style: s.input + ';background:#fff;cursor:pointer'
                  }, [
                    h('option', { value: 0 }, 'No alert'),
                    h('option', { value: 1 }, '1 day before'),
                    h('option', { value: 3 }, '3 days before'),
                    h('option', { value: 7 }, '7 days before'),
                    h('option', { value: 14 }, '14 days before'),
                  ])
                ]),
                h('div', null, [
                  h('div', { style: fieldLabelStyle }, ['Custom number of days ', h('span', { style: 'color:#DC2626' }, '*')]),
                  h('input', {
                    type: 'number', min: 0, max: 999,
                    value: advExpirationAlertDays.value,
                    onInput: e => { advExpirationAlertDays.value = Math.max(0, parseInt(e.target.value) || 0) },
                    style: s.input
                  })
                ])
              ])
            ]),

            // Template Password
            h('div', { id: 'env-adv-section-template-password', style: sectionStyle }, [
              h('h2', { style: titleStyle }, 'Envelope Password'),
              h('p', { style: subtitleStyle }, 'Use a password to protect this envelope.'),
              h('div', { style: 'display:flex;align-items:center;gap:10px;margin-bottom:16px' }, [
                dsToggle(advPasswordEnabled.value, v => { advPasswordEnabled.value = v }),
                h('span', { style: 'font-size:14px;color:#374151' }, 'Turn on password protection')
              ]),
              advPasswordEnabled.value && h('div', { style: 'max-width:320px' }, [
                h('div', { style: fieldLabelStyle }, 'Password'),
                h('input', {
                  type: 'password', value: advPassword.value,
                  placeholder: 'Enter password',
                  onInput: e => { advPassword.value = e.target.value },
                  style: s.input
                })
              ])
            ]),

            // Template Modification
            h('div', { id: 'env-adv-section-template-modification', style: sectionStyle }, [
              h('h2', { style: titleStyle }, 'Envelope Modification'),
              h('p', { style: subtitleStyle }, 'Limit the changes someone can make when they use this envelope.'),
              h('label', { style: 'display:flex;align-items:center;gap:10px;cursor:pointer;margin-bottom:12px' }, [
                h('input', { type: 'checkbox', checked: advAllowRecipientsEdit.value, onChange: e => { advAllowRecipientsEdit.value = e.target.checked }, style: `width:16px;height:16px;accent-color:${C.primary}` }),
                h('div', null, [
                  h('div', { style: 'font-size:14px;color:#1a1a2e' }, 'Allow senders to edit, add, or remove recipients'),
                ])
              ]),
              h('label', { style: 'display:flex;align-items:center;gap:10px;cursor:pointer' }, [
                h('input', { type: 'checkbox', checked: advAllowMessageEdit.value, onChange: e => { advAllowMessageEdit.value = e.target.checked }, style: `width:16px;height:16px;accent-color:${C.primary}` }),
                h('div', null, [
                  h('div', { style: 'font-size:14px;color:#1a1a2e' }, 'Allow senders to edit the subject, email, or private messages'),
                ])
              ]),
            ]),
          ])
        ]),

        // Save button
        h('div', { style: 'border-top:1px solid #E5E7EB;padding:14px 24px;display:flex;justify-content:flex-end;flex-shrink:0;background:#fff' }, [
          h('button', {
            onClick: () => { showAdvanced.value = false },
            style: `padding:10px 32px;background:${C.primary};color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit`
          }, 'Save')
        ])
      ])
    }

    const CATEGORIES = ['Agency Agreements','Bank Account Opening Agreements','Confidentiality Agreements','Consulting Agreements','Contractor Agreements','Credit Card Agreements','Distribution Agreements','Employment Contracts','Franchise Agreements','Independent Contractor Agreements','Intellectual Property Assignment Agreements','Investment Account Agreements','Joint Venture Agreements','Lease Agreements','Licensing Agreements','Loan Agreements','Loan Applications','Non-Disclosure Agreements (NDAs)','Onboarding Agreements','Other','Partnership Agreements','Purchase Agreements','Sales Contracts','Service Agreements','Software License Agreements','Vendor Agreements','Wealth Management Agreements']


    return () => h('div', null, [

      // Advanced Options full panel
      showAdvanced.value && renderAdvancedPanel(),

      // Main modal
      h('div', {
        style: s.overlay,
        onClick: (e) => {
          if (e.target === e.currentTarget) props.onClose?.()
          showUploadDropdown.value = false
        }
      }, [
        h('div', { style: s.modal, onClick: () => { showUploadDropdown.value = false } }, [

          h('div', { onClick: e => e.stopPropagation() }, [
            modalHeader({
              iconName: 'mail',
              title: 'Create Envelope',
              subtitle: 'Send documents for signature.',
              onAdvanced: () => { showAdvanced.value = true },
              onClose: props.onClose
            })
          ]),

          // Step bar
          h('div', { style: `display:flex;align-items:center;padding:12px 28px;border-bottom:1px solid ${C.border};gap:0;flex-shrink:0;background:#FAFAFC` },
            ['Add Documents & Recipients', 'Place Fields & Send'].map((label, i) => {
              const n = i + 1
              const active = step.value === n
              const done = step.value > n
              return h('div', { key: i, style: 'display:flex;align-items:center;' + (i < 1 ? 'flex:1' : '') }, [
                h('div', { style: 'display:flex;align-items:center;gap:6px;flex-shrink:0' }, [
                  h('div', {
                    style: `width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px;` +
                      `background:${done ? '#059669' : active ? C.primary : C.border};` +
                      `color:${(done || active) ? '#fff' : C.muted}`
                  }, done ? icon('check', 13) : String(n)),
                  h('span', {
                    style: `font-size:13px;font-weight:${active ? 700 : 400};` +
                      `color:${active || done ? C.text : C.muted};white-space:nowrap`
                  }, label)
                ]),
                i < 1 && h('div', { style: `flex:1;height:2px;margin:0 12px;background:${done ? '#059669' : C.border}` })
              ])
            })
          ),

          h('div', { style: s.body }, [
            h('div', { style: 'display:flex;flex-direction:column' }, [

              // No envelope name field: it only ever echoed the first document's name, and the
              // server names the envelope after that document anyway
              // (EnvelopesController#prepare). Classification is the one thing only the sender
              // knows, so that is all this section asks for.
              section({ icon: 'file_text', title: 'Envelope details', subtitle: 'Classify this envelope.' }, [
                h('div', null, [
                  fieldLabel('Envelope category'),
                  h('select', {
                    value: envelopeCategory.value,
                    onChange: e => { envelopeCategory.value = e.target.value; if (e.target.value !== 'Other') envelopeType.value = '' },
                    style: s.input + ';cursor:pointer'
                  }, [
                    h('option', { value: '' }, '-- Select --'),
                    ...CATEGORIES.map(cat => h('option', { value: cat }, cat))
                  ]),
                  envelopeCategory.value === 'Other' ? h('div', { style: 'margin-top:12px' }, [
                    fieldLabel('Envelope type', true),
                    h('p', { style: `font-size:12px;color:${C.muted};margin:0 0 6px` }, 'Do not enter personal data or other PII.'),
                    h('input', {
                      value: envelopeType.value,
                      placeholder: 'Enter envelope type...',
                      maxlength: 100,
                      onInput: e => { envelopeType.value = e.target.value },
                      style: s.input
                    }),
                    h('div', { style: `text-align:right;font-size:12px;color:${C.muted};margin-top:4px` }, String(envelopeType.value.length) + '/100')
                  ]) : null
                ])
              ]),

              section({ icon: 'upload', title: 'Add documents', subtitle: 'Upload a PDF or choose an existing template.' }, [
                dropzoneShell([
                  h('div', { style: `margin-bottom:10px;display:flex;justify-content:center;color:${C.primary}` }, icon('upload', 28)),
                  h('div', { style: `font-size:13px;color:${C.muted};margin-bottom:14px` }, 'Drop your files here or'),
                  uploadSplitButton({
                    onUpload: () => fileInput.value?.click(),
                    onToggle: (e) => { e.stopPropagation(); showUploadDropdown.value = !showUploadDropdown.value },
                    open: showUploadDropdown.value,
                    menuItems: [
                      menuItem('From Computer', 'folder', () => { fileInput.value?.click() }),
                      h('div', { style: `height:1px;background:${C.border};margin:0 12px` }),
                      menuItem('Use a Template', 'file_text', openTemplatePicker)
                    ]
                  }),
                  h('input', { ref: fileInput, type: 'file', multiple: true, accept: '.pdf', style: 'display:none', onChange: e => addFiles(e.target.files) })
                ], { onDrop: e => { e.preventDefault(); addFiles(e.dataTransfer.files) } }),

                (docs.value.length > 0 || selectedExistingTemplate.value.length > 0) && h('div', { style: 'display:flex;gap:12px;flex-wrap:wrap' }, [
                  ...docs.value.map(d =>
                    h('div', { key: d.id, style: `width:150px;border:1px solid ${C.border};border-radius:10px;overflow:hidden;background:#fff;position:relative` }, [
                      h('div', { style: `height:90px;background:${C.primarySoft};display:flex;align-items:center;justify-content:center;color:${C.primary}` }, icon('file_text', 30)),
                      h('div', { style: 'padding:8px 10px' }, [
                        h('div', { style: `font-size:12px;font-weight:600;color:${C.text};overflow:hidden;text-overflow:ellipsis;white-space:nowrap`, title: d.name }, d.name),
                      ]),
                      h('button', {
                        onClick: () => { removeDoc(d.id); selectionOrder.value = selectionOrder.value.filter(s => !(s.type === 'file' && s.id === d.id)) },
                        style: 'position:absolute;top:5px;right:5px;width:20px;height:20px;border-radius:4px;border:none;background:rgba(0,0,0,.4);color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center'
                      }, icon('x', 14))
                    ])
                  ),
                  ...selectedExistingTemplate.value.map((t, i) =>
                    h('div', {
                      key: 'tpl-' + t.id,
                      draggable: true,
                      onDragstart: () => onTplDragStart(i),
                      onDragover: e => onTplDragOver(e, i),
                      onDragend: onTplDragEnd,
                      style: `min-width:180px;flex:1;max-width:280px;border:1.5px solid ${C.primary};border-radius:10px;background:${C.primarySoft};padding:10px 12px;display:flex;align-items:center;gap:10px;cursor:move;opacity:${tplDragIndex.value === i ? '.4' : '1'}`
                    }, [
                      h('span', { style: `color:${C.muted};display:flex` }, icon('grip', 16)),
                      h('span', { style: `width:22px;height:22px;border-radius:50%;background:${C.primary};color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0` }, String(i + 1)),
                      h('div', { style: `flex:1;font-size:13px;font-weight:600;color:${C.text};overflow:hidden;text-overflow:ellipsis;white-space:nowrap` }, t.name),
                      h('button', {
                        onClick: () => toggleTemplateSelection(t),
                        style: s.btnGhost
                      }, [icon('x', 13)])
                    ])
                  )
                ]),

                showTemplatePicker.value && h('div', {
                  style: 'position:fixed;inset:0;background:rgba(15,23,42,.45);z-index:10000;display:flex;align-items:center;justify-content:center',
                  onClick: e => e.target === e.currentTarget && (showTemplatePicker.value = false)
                }, [
                  h('div', { style: `background:#fff;border-radius:14px;width:560px;max-height:70vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 20px 50px rgba(0,0,0,.2)` }, [
                    h('div', { style: `padding:20px 24px;border-bottom:1px solid ${C.border};display:flex;align-items:center;justify-content:space-between` }, [
                      h('div', { style: `font-size:15px;font-weight:700;color:${C.text}` }, 'Choose Templates'),
                      h('button', { onClick: () => showTemplatePicker.value = false, style: `width:28px;height:28px;border:1px solid ${C.border};border-radius:8px;background:none;cursor:pointer;color:${C.muted}` }, icon('x', 14))
                    ]),
                    h('div', { style: `padding:12px 24px;border-bottom:1px solid ${C.border}` }, [
                      h('input', { value: templateSearch.value, placeholder: 'Search templates...', onInput: e => { templateSearch.value = e.target.value; filterTemplates() }, style: s.input })
                    ]),
                    h('div', { style: 'flex:1;overflow:auto;padding:12px 24px;display:flex;flex-direction:column;gap:8px' }, [
                      loadingTemplates.value
                        ? h('div', { style: `text-align:center;padding:24px;color:${C.muted};font-size:13px` }, 'Loading templates...')
                        : filteredTemplates.value.length === 0
                          ? h('div', { style: `text-align:center;padding:24px;color:${C.muted};font-size:13px` }, 'No templates found')
                          : filteredTemplates.value.map(t => {
                              const selIdx = selectedExistingTemplate.value.findIndex(x => x.id === t.id)
                              const isSel = selIdx !== -1
                              return h('div', {
                                key: t.id, onClick: () => toggleTemplateSelection(t),
                                style: `display:flex;align-items:center;gap:12px;padding:12px 14px;border:${isSel ? '1.5px solid ' + C.primary : '1px solid ' + C.border};border-radius:10px;cursor:pointer;background:${isSel ? C.primarySoft : '#fff'}`
                              }, [
                                h('span', { style: `display:flex;color:${C.primary}` }, icon('file_text', 18)),
                                h('div', { style: 'flex:1' }, [
                                  h('div', { style: `font-size:13px;font-weight:600;color:${C.text}` }, t.name),
                                  h('div', { style: 'font-size:11px;color:#9CA3AF;margin-top:2px' }, [t.author, t.created_at ? new Date(t.created_at).toLocaleString() : ''].filter(Boolean).join(' · '))
                                ]),
                                isSel
                                  ? h('span', { style: `width:22px;height:22px;border-radius:50%;background:${C.primary};color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0` }, String(selIdx + 1))
                                  : h('span', { style: `font-size:12px;color:${C.primary};font-weight:600` }, 'Select')
                              ])
                            })
                    ]),
                    h('div', { style: `padding:12px 24px;border-top:1px solid ${C.border};display:flex;align-items:center;justify-content:space-between` }, [
                      h('div', { style: `font-size:12px;color:${C.muted}` }, `${selectedExistingTemplate.value.length} selected`),
                      h('button', { onClick: () => { showTemplatePicker.value = false; templateSearch.value = '' }, style: s.btn }, 'Done')
                    ])
                  ])
                ])
              ]),

              selectedExistingTemplate.value.length > 0
                ? section({ icon: 'users', title: 'Add recipients', subtitle: 'Add everyone who needs to sign or receive this envelope.' }, [renderRoleMapping()])
                : section({ icon: 'users', title: 'Add recipients', subtitle: 'Add everyone who needs to sign or receive this envelope.' }, [
                  h('label', { style: 'display:flex;align-items:flex-start;gap:8px;cursor:pointer;width:fit-content' }, [
                    h('input', { type: 'checkbox', checked: signingOrderEnabled.value, onChange: e => { signingOrderEnabled.value = e.target.checked }, style: `width:15px;height:15px;margin-top:2px;cursor:pointer;accent-color:${C.primary}` }),
                    h('div', null, [
                      h('div', { style: `font-size:13px;font-weight:600;color:${C.text}` }, 'Set signing order'),
                      h('div', { style: `font-size:12px;color:${C.muted};margin-top:2px` }, 'Recipients will be asked to sign in the order listed below.')
                    ])
                  ]),
                  h('div', { style: s.tableWrap }, [
                    h('div', { style: s.tableHead + `;grid-template-columns:${plainCols}` }, [
                      h('span', null, 'Order'), h('span', null, 'Name *'), h('span', null, 'Email *'),
                      h('span', null, 'Signer'), h('span', null, 'Action'), h('span', null, '')
                    ]),
                    ...recipients.value.map((r, i) =>
                      h('div', {
                        key: r.id,
                        draggable: signingOrderEnabled.value,
                        onDragstart: () => signingOrderEnabled.value && onDragStart(i),
                        onDragover: (e) => signingOrderEnabled.value && onDragOver(e, i),
                        onDragend: onDragEnd,
                        style: s.tableRow + `;grid-template-columns:${plainCols};${i === recipients.value.length - 1 ? 'border-bottom:none' : ''}`
                      }, [
                        orderCell(recipients, r, i),
                        h('input', { value: r.name, placeholder: 'Full name', onInput: e => { r.name = e.target.value }, style: s.input }),
                        h('input', { value: r.email, placeholder: 'email@example.com', type: 'email', onInput: e => { r.email = e.target.value }, style: s.input }),
                        // Signing role. The person is the Name/Email either side of it; this is
                        // the role the template's fields are placed against.
                        signerRoleSelect(r.roleName, name => { r.roleName = name; applyRoleDefaults(r, name) }),
                        h('select', { value: r.role, onChange: e => { r.role = e.target.value }, style: s.input + ';cursor:pointer' }, [
                          h('option', { value: 'signer' }, 'Needs to Sign'),
                          h('option', { value: 'cc' }, 'Receives a Copy'),
                          h('option', { value: 'viewer' }, 'Needs to View'),
                        ]),
                        deleteRecipientButton(() => removeRecipient(r.id))
                      ])
                    )
                  ]),
                  h('button', { onClick: addRecipient, style: s.addLink }, [icon('plus', 14), ' Add Recipient'])
                ]),

              section({ icon: 'message', title: 'Add message', subtitle: 'Optional email subject and body for invites.' }, [
                messageTemplateControls({
                  templatesRef: messageTemplates,
                  messageRef: message,
                  saveStateRef: messageSaveState,
                  onSave: saveCurrentMessage
                }),
                h('div', null, [
                  fieldLabel('Subject line'),
                  h('input', { value: message.value.subject, placeholder: 'Please Complete DocuSeal – Document', onInput: e => { message.value.subject = e.target.value }, style: s.input })
                ]),
                h('div', null, [
                  fieldLabel('Message'),
                  h('textarea', { value: message.value.body, rows: 3, placeholder: 'Please review and sign the attached document.', onInput: e => { message.value.body = e.target.value }, style: s.input + ';resize:vertical' })
                ])
              ]),

              h('div', { style: `background:${C.primarySoft};border:1px solid ${C.primaryBorder};border-radius:10px;padding:14px 16px;display:flex;gap:10px;margin:8px 0 16px` }, [
                h('span', { style: `display:flex;color:${C.primary}` }, icon('info_circle', 18)),
                h('div', null, [
                  h('div', { style: `font-size:13px;font-weight:600;color:${C.text};margin-bottom:2px` }, 'What happens next?'),
                  h('div', { style: `font-size:12px;color:${C.text}` }, 'After clicking Next, your document will open in the editor where you can place signature fields — then send directly to your recipients.')
                ])
              ])
            ])
          ]),

          h('div', { style: s.footer }, [
            h('button', { onClick: props.onClose, style: s.btnSec }, 'Cancel'),
            h('button', {
              onClick: proceedToEditor,
              disabled: !canNext.value || submitting.value,
              style: s.btn + `;opacity:${canNext.value && !submitting.value ? 1 : .5};cursor:${canNext.value && !submitting.value ? 'pointer' : 'not-allowed'}`
            }, submitting.value ? 'Opening editor…' : 'Next: Place Fields')
          ])
        ])
      ])
    ])
  }
})
